import { config } from '../config';
import {
  approvalHeaders,
  readAdminError,
  type Approval,
  type ApprovalRequest,
} from './approvalRequests';
import { getAdminApiAuthHeaders } from './auth';

export interface ContentItem {
  id: string;
  content_type: string;
  key: string;
  locale: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  fields: Record<string, unknown>;
  version: number;
  route_display_name?: string;
  route_path?: string;
  route_mode?: 'CURRENT' | 'VERSIONED';
  route_version?: string;
  version_query_params?: string[];
  screen_id?: string;
  tags: string[];
  published_at?: string;
  published_by?: string;
  created_at: string;
  updated_at: string;
  /** DM-7214: the item's proposed revision, when one is stored on the server. */
  pending_revision?: ContentRevisionSummary;
}

/** The editable part of a content item, as a proposed revision carries it. */
export interface ContentRevisionProposal {
  fields: Record<string, unknown>;
  route_display_name?: string;
  route_path?: string;
  route_mode?: 'CURRENT' | 'VERSIONED';
  route_version?: string;
  version_query_params?: string[];
  screen_id?: string;
  tags: string[];
}

export interface ContentRevisionSummary {
  revision_id: string;
  request_id: string;
  author_id: string;
  base_version: number;
  hash: string;
  created_at: string;
  /** True when the live item has moved on since the revision was proposed. */
  stale: boolean;
}

export interface ContentRevision {
  content_id: string;
  revision_id: string;
  base_version: number;
  hash: string;
  author_id: string;
  created_at: string;
  change_note?: string;
  request_id: string;
  proposal: ContentRevisionProposal;
}

export interface ContentRevisionDetail {
  revision: ContentRevision;
  base: ContentItem;
  stale: boolean;
}

export interface ProposedContentRevision {
  revision: ContentRevision;
  request: ApprovalRequest;
}

export type ContentUpdates = Partial<{
  routeDisplayName: string;
  routePath: string;
  routeMode: 'CURRENT' | 'VERSIONED';
  routeVersion: string;
  versionQueryParams: string[];
  screenId: string;
  fields: Record<string, unknown>;
  tags: string[];
  changeNote: string;
}>;

interface ApiEnvelope<T> {
  data?: T;
}

type RawContentItem = Partial<ContentItem> & {
  contentType?: string;
  routeDisplayName?: string;
  routePath?: string;
  routeMode?: 'CURRENT' | 'VERSIONED';
  routeVersion?: string;
  versionQueryParams?: string[];
  screenId?: string;
  publishedAt?: string;
  publishedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  pendingRevision?: {
    revisionId: string;
    requestId: string;
    authorId: string;
    baseVersion: number;
    hash: string;
    createdAt: string;
    stale: boolean;
  };
};

function unwrap<T>(data: T | ApiEnvelope<T>): T {
  return (data as ApiEnvelope<T>).data || (data as T);
}

function normalizeContentItem(raw: RawContentItem): ContentItem {
  return {
    id: raw.id || '',
    content_type: raw.content_type || raw.contentType || '',
    key: raw.key || '',
    locale: raw.locale || 'en',
    status: raw.status || 'DRAFT',
    fields: raw.fields || {},
    version: raw.version || 1,
    route_display_name: raw.route_display_name || raw.routeDisplayName,
    route_path: raw.route_path || raw.routePath,
    route_mode: raw.route_mode || raw.routeMode,
    route_version: raw.route_version || raw.routeVersion,
    version_query_params: raw.version_query_params || raw.versionQueryParams,
    screen_id: raw.screen_id || raw.screenId,
    tags: raw.tags || [],
    published_at: raw.published_at || raw.publishedAt,
    published_by: raw.published_by || raw.publishedBy,
    created_at: raw.created_at || raw.createdAt || '',
    updated_at: raw.updated_at || raw.updatedAt || '',
    pending_revision: raw.pending_revision || normalizePendingRevision(raw.pendingRevision),
  };
}

function normalizePendingRevision(
  raw: RawContentItem['pendingRevision']
): ContentRevisionSummary | undefined {
  if (!raw) return undefined;
  return {
    revision_id: raw.revisionId,
    request_id: raw.requestId,
    author_id: raw.authorId,
    base_version: raw.baseVersion,
    hash: raw.hash,
    created_at: raw.createdAt,
    stale: raw.stale,
  };
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    return body?.detail || body?.message || body?.data?.detail || fallback;
  } catch {
    return fallback;
  }
}

function revisionUrl(id: string, revisionId: string): string {
  return `${config.api.url}/admin/content/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revisionId)}`;
}

export const contentApi = {
  async list(contentType?: string, locale?: string): Promise<ContentItem[]> {
    const headers = await getAdminApiAuthHeaders();
    const url = new URL(`${config.api.url}/admin/content`);
    if (contentType) url.searchParams.set('contentType', contentType);
    if (locale) url.searchParams.set('locale', locale);
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) throw new Error(await readError(res, 'Failed to list content'));
    const data = unwrap<{ items?: RawContentItem[] }>(await res.json());
    return (data.items || []).map(normalizeContentItem);
  },

  async get(id: string): Promise<ContentItem> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content/${id}`, { headers });
    if (!res.ok) throw new Error(await readError(res, 'Failed to get content'));
    return normalizeContentItem(unwrap<RawContentItem>(await res.json()));
  },

  async create(content: {
    contentType: string;
    key: string;
    locale: string;
    routeDisplayName?: string;
    routePath?: string;
    routeMode?: 'CURRENT' | 'VERSIONED';
    routeVersion?: string;
    versionQueryParams?: string[];
    screenId?: string;
    fields: Record<string, unknown>;
    tags?: string[];
  }): Promise<ContentItem> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content`, {
      method: 'POST',
      headers,
      body: JSON.stringify(content),
    });
    if (!res.ok) throw new Error(await readError(res, 'Failed to create content'));
    return normalizeContentItem(unwrap<RawContentItem>(await res.json()));
  },

  /**
   * Draft items only. DM-7214: the server refuses this on a live item
   * (LIVE_ROUTE_REQUIRES_REVISION); a live edit goes through proposeRevision.
   */
  async update(id: string, updates: ContentUpdates): Promise<ContentItem> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error(await readError(res, 'Failed to update content'));
    return normalizeContentItem(unwrap<RawContentItem>(await res.json()));
  },

  /**
   * DM-7214: store an edit to a live item on the server as its proposed
   * revision and open the approval request for exactly that content. From
   * here on nothing depends on this tab staying open.
   */
  async proposeRevision(
    id: string,
    updates: ContentUpdates,
    reason: string,
    note?: string
  ): Promise<ProposedContentRevision> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content/${encodeURIComponent(id)}/revisions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...updates, reason, note }),
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to propose the revision');
    return unwrap<ProposedContentRevision>(await res.json());
  },

  async getRevision(id: string, revisionId: string): Promise<ContentRevisionDetail> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(revisionUrl(id, revisionId), { headers });
    if (!res.ok) throw await readAdminError(res, 'Failed to load the proposed revision');
    const detail = unwrap<Omit<ContentRevisionDetail, 'base'> & { base: RawContentItem }>(
      await res.json()
    );
    return { ...detail, base: normalizeContentItem(detail.base) };
  },

  /** Publishes what the server stored and the approver reviewed. Sends no content. */
  async publishRevision(id: string, revisionId: string, approval: Approval): Promise<ContentItem> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${revisionUrl(id, revisionId)}/publish`, {
      method: 'POST',
      headers: { ...headers, ...approvalHeaders(approval) },
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to publish the revision');
    return normalizeContentItem(unwrap<RawContentItem>(await res.json()));
  },

  async discardRevision(id: string, revisionId: string): Promise<void> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(revisionUrl(id, revisionId), { method: 'DELETE', headers });
    if (!res.ok) throw await readAdminError(res, 'Failed to discard the revision');
  },

  async publish(id: string, approval: Approval): Promise<ContentItem> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content/${id}/publish`, {
      method: 'POST',
      headers: { ...headers, ...approvalHeaders(approval) },
    });
    if (!res.ok) throw new Error(await readError(res, 'Failed to publish content'));
    return normalizeContentItem(unwrap<RawContentItem>(await res.json()));
  },

  async remove(id: string, approval: Approval): Promise<void> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/content/${id}`, {
      method: 'DELETE',
      headers: { ...headers, ...approvalHeaders(approval) },
    });
    if (!res.ok) throw new Error(await readError(res, 'Failed to delete content'));
  },
};
